import { UserProject } from '@app/@types/redux/project';
import {
  WorkspaceResponseListItem,
  type WorkspaceFilters as WorkspaceFiltersType,
} from '@app/@types/redux/workspace';
import { slugify } from '@libs/helper';
import { nanoid } from '@reduxjs/toolkit';
import { fetchMyWorkspaces } from '@state/redux/slices/workspace.slice';
import { useAppDispatch, useAppSelector } from '@state/redux/store';
import classnames from 'classnames';
import _ from 'lodash';
import { DropdownMenu } from 'radix-ui';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { BsThreeDots } from 'react-icons/bs';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { FiAlertTriangle } from 'react-icons/fi';
import { PiArrowSquareInDuotone, PiPlayDuotone, PiPresentationChartDuotone } from 'react-icons/pi';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import { Alert, Card, CardBody } from 'reactstrap';
import { SORT_DIR, TITLE } from '../../libs/constants/constants';
import { i18nCText } from '../../libs/i18n/I18n';
import Api from '../../state/utils/Api';
import { translate } from '../../state/utils/helper';
import { Col, Container, Image, Pagination, PaginationItem, PaginationLink, Row } from '../UI/Html';
import Layout from '../UI/Layout';
import Footer from '../UI/Layout/Footer';
import './Workspace.scss';
import WorkspaceCarousel from './WorkspaceCarousel';
import WorkspaceFilters, { PROJECT_SORT_BY, WORKSPACE_SORT_BY } from './WorkspaceFilters';
import WorkspaceItem from './WorkspaceItem';

/**
 * Renders a placeholder skeleton for loading states.
 *
 * This function returns a React fragment containing six columns. Each column includes
 * several skeleton elements that mimic the layout and spacing of the actual content, providing
 * visual feedback while data is being loaded.
 *
 * @returns A JSX fragment with skeleton placeholders.
 */
function loadingSkeleton() {
  return (
    <>
      {Array.from(Array(6), () => (
        <Col xs={12} sm={12} md={6} lg={3} xl={2} className="mb-5" key={nanoid()}>
          <Skeleton height={150} />
          <div style={{ height: 20 }} />
          <Skeleton count={1} height={20} />
          <div style={{ height: 20 }} />
          <Skeleton count={3} height={20} />
        </Col>
      ))}
    </>
  );
}

const ListWorkspaces = () => {
  const [loading, setLoading] = useState(true);
  const [loadingActiveProjects, setLoadingActiveProjects] = useState(true);
  const [loadingFinishedProjects, setLoadingFinishedProjects] = useState(true);
  const [totalPagesActiveChallenge, setTotalPagesActiveChallenge] = useState(0);
  const [totalPageFinishedProjects, setTotalPageFinishedProjects] = useState(0);

  const [params, setParams] = useState({
    page: 1,
    perPage: 18,
  });

  const [paramsActiveProjects, setParamsActiveProjects] = useState({
    page: 1,
    perPage: 18,
  });

  const [paramsFinishedProjects, setParamsFinishedProjects] = useState({
    page: 1,
    perPage: 18,
  });

  const reduxWorkspaces = useAppSelector((state) => state.workspaces.workspaces);
  const reduxWorkspacesCount = useAppSelector((state) => state.workspaces.totalWorkspaces);
  const totalPages = Math.ceil(reduxWorkspacesCount / params.perPage);

  const dispatch = useAppDispatch();
  const [workspaces, setWorkspaces] = useState(reduxWorkspaces);
  const [activeProjects, setActiveProjects] = useState<UserProject[]>([]);
  const [finishedProjects, setFinishedProjects] = useState<UserProject[]>([]);
  const [tab, setTab] = useState('workspaces');

  const [workspaceFilters, setWorkspaceFilters] = useState<WorkspaceFiltersType>({
    sortBy: WORKSPACE_SORT_BY.WORKSPACE_START_DATE,
    sortDirection: SORT_DIR.DESC,
  });

  const [projectFilters, setProjectFilters] = useState<WorkspaceFiltersType>({
    sortBy: PROJECT_SORT_BY.PROJECT_LAST_ACTIVE,
    sortDirection: SORT_DIR.DESC,
  });

  useEffect(() => {
    setWorkspaces(reduxWorkspaces);
  }, [reduxWorkspaces]);

  const fetchProjects = useCallback(
    (
      pageNo?: number,
      sortBy = projectFilters.sortBy,
      sortDirection = projectFilters.sortDirection
    ) => {
      setLoadingActiveProjects(true);
      const filter = {};
      Api.getMyProjects(
        pageNo ?? 1,
        paramsActiveProjects.perPage,
        sortDirection,
        sortBy,
        JSON.stringify(filter)
      )
        .then((res) => {
          setActiveProjects(res.data.data);
          setTotalPagesActiveChallenge(
            Math.round(res.data.meta.total / paramsActiveProjects.perPage)
          );
          setLoadingActiveProjects(false);
        })
        .catch(() => {
          setLoadingActiveProjects(false);
        });
    },
    [paramsActiveProjects.perPage, projectFilters.sortBy, projectFilters.sortDirection]
  );

  const fetchFinishedProjects = useCallback(
    (
      pageNo?: number,
      sortBy = projectFilters.sortBy,
      sortDirection = projectFilters.sortDirection
    ) => {
      setLoadingFinishedProjects(true);
      Api.getMyProjects(
        pageNo ?? 1,
        paramsFinishedProjects.perPage,
        sortDirection,
        sortBy,
        JSON.stringify({ progress_eq: '1' })
      )
        .then((res) => {
          setFinishedProjects(res.data.data);
          setTotalPageFinishedProjects(
            Math.round(res.data.meta.total / paramsFinishedProjects.perPage)
          );
          setLoadingFinishedProjects(false);
        })
        .catch(() => {
          setLoadingFinishedProjects(false);
        });
    },
    [paramsFinishedProjects.perPage, projectFilters.sortBy, projectFilters.sortDirection]
  );

  useEffect(() => {
    if (tab === 'workspaces') {
      setWorkspaces(reduxWorkspaces);
    } else if (tab === 'activeProjects') {
      fetchProjects(1);
    } else if (tab === 'finishedProjects') {
      fetchFinishedProjects();
    }
  }, [fetchFinishedProjects, fetchProjects, reduxWorkspaces, tab]);

  const fetchWorkspace = useCallback(
    (
      pageNo: number,
      sortBy = workspaceFilters.sortBy,
      sortDirection = workspaceFilters.sortDirection
    ) => {
      setLoading(true);
      dispatch(
        fetchMyWorkspaces({
          currentPage: pageNo,
          perPage: params.perPage,
          sortOption: sortBy,
          sortDirection,
        })
      )
        .then(() => {
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    },
    [dispatch, params.perPage, workspaceFilters.sortBy, workspaceFilters.sortDirection]
  );

  useEffect(() => {
    fetchWorkspace(params.page, workspaceFilters.sortBy, workspaceFilters.sortDirection);
  }, [fetchWorkspace, params.page, workspaceFilters.sortBy, workspaceFilters.sortDirection]);

  const handlePageChange = (v: number) => {
    fetchWorkspace(v);
  };

  useEffect(() => {
    if (tab === 'activeProjects') {
      fetchProjects(paramsActiveProjects.page, projectFilters.sortBy, projectFilters.sortDirection);
    } else if (tab === 'finishedProjects') {
      fetchFinishedProjects(
        paramsFinishedProjects.page,
        projectFilters.sortBy,
        projectFilters.sortDirection
      );
    } else {
      fetchWorkspace(params.page, workspaceFilters.sortBy, workspaceFilters.sortDirection);
    }
  }, [
    fetchFinishedProjects,
    fetchProjects,
    fetchWorkspace,
    params.page,
    paramsActiveProjects.page,
    paramsFinishedProjects.page,
    projectFilters.sortBy,
    projectFilters.sortDirection,
    tab,
    workspaceFilters.sortBy,
    workspaceFilters.sortDirection,
  ]);

  return (
    <Layout>
      <Helmet>
        <title>{`${translate('layout.navbar.navlinks.projects')} | ${TITLE}`}</title>
      </Helmet>
      <div className="hero_banner_block">
        <Container fluid>
          <Row className="px-3">
            <Col xs={12} sm={12} md={12} lg={12} xl={12}>
              <WorkspaceCarousel />
              <WorkspaceFilters
                tab={tab}
                setTab={setTab}
                workspaceFilters={workspaceFilters}
                setWorkspaceFilters={setWorkspaceFilters}
                projectFilters={projectFilters}
                setProjectFilters={setProjectFilters}
              />
            </Col>
          </Row>
        </Container>
      </div>
      <Container fluid>
        {tab === 'workspaces' ? (
          <Row className="mt-4 main-container">
            <Col>
              <Row className="m0-2">
                <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                  {loading ? (
                    <Row>{loadingSkeleton()}</Row>
                  ) : (
                    <WorkspaceList workspaces={workspaces} />
                  )}
                </Col>
                <Col>
                  {totalPages > 0 && (
                    <FooterPagination
                      params={params}
                      setParams={setParams}
                      handlePageChange={handlePageChange}
                      totalPages={totalPages}
                    />
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        ) : null}
        {tab === 'activeProjects' ? (
          <ProjectList
            projects={activeProjects}
            loading={loadingActiveProjects}
            totalPages={totalPagesActiveChallenge}
            params={paramsActiveProjects}
            setParams={setParamsActiveProjects}
            handlePageChange={fetchProjects}
          />
        ) : null}
        {tab === 'finishedProjects' ? (
          <ProjectList
            projects={finishedProjects}
            loading={loadingFinishedProjects}
            totalPages={totalPageFinishedProjects}
            params={paramsFinishedProjects}
            setParams={setParamsFinishedProjects}
            handlePageChange={fetchFinishedProjects}
          />
        ) : null}
        <Footer />
      </Container>
    </Layout>
  );
};

interface FooterProps {
  params: {
    page: number;
    perPage: number;
  };
  setParams: (params: { page: number; perPage: number }) => void;
  handlePageChange: (page: number) => void;
  totalPages: number;
}

const FooterPagination = (props: FooterProps) => {
  const { params, setParams, handlePageChange, totalPages } = props;
  const { page } = params;

  const pages = totalPages;

  function switchPage(pageNumber: number) {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setParams({
        ...params,
        page: pageNumber,
      });
      handlePageChange(pageNumber);
    }
  }

  const previousPage = params.page - 1;
  const nextPage = params.page + 1;

  function renderPages() {
    const currentPageIndex = page;
    return _.range(1, pages + 1).map((p, index) => {
      const pId = p;
      const element = (
        <PaginationItem key={pId} className={page === pId ? 'active' : ''}>
          <PaginationLink
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              switchPage(pId);
            }}
          >
            {index + 1}
          </PaginationLink>
        </PaginationItem>
      );
      if (
        index === 0 ||
        pages - 1 === index ||
        (currentPageIndex - 3 < index && currentPageIndex + 3 > index)
      ) {
        return element;
      }
      if (currentPageIndex - 3 === index || currentPageIndex + 3 === index) {
        return (
          <PaginationItem key={pId} className="disabled">
            <PaginationLink
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
              }}
            >
              ...
            </PaginationLink>
          </PaginationItem>
        );
      }
      return '';
    });
  }

  return (
    <div className="float-right pagination-style">
      <Pagination
        className={classnames('pagination justify-content-end mb-0')}
        listClassName="justify-content-end mb-0"
      >
        <PaginationItem className={previousPage ? '' : 'disabled'}>
          <PaginationLink
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              switchPage(previousPage);
            }}
            tabIndex="-1"
          >
            <FaAngleLeft />
            <span className="sr-only">{translate('takeProject.previous')}</span>
          </PaginationLink>
        </PaginationItem>
        {renderPages()}
        <PaginationItem className={nextPage ? '' : 'disabled'}>
          <PaginationLink
            href="#"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              switchPage(nextPage);
            }}
          >
            <FaAngleRight />
            <span className="sr-only">{translate('takeProject.next')}</span>
          </PaginationLink>
        </PaginationItem>
      </Pagination>
    </div>
  );
};

/**
 * Renders a responsive grid of workspace items or an alert if none are available.
 *
 * This component displays workspaces using a grid layout. Each workspace is rendered
 * as a WorkspaceItem within a responsive column that includes a fade-in animation delay.
 * If the provided workspace list is empty, it shows a centered alert message informing
 * the user that there are no workspaces available.
 *
 * @param props - Contains the list of workspace items to display.
 */
function WorkspaceList(props: { workspaces: WorkspaceResponseListItem[] }) {
  const { workspaces } = props;
  if (workspaces.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center my-6">
        <Alert color="default">
          <span className="alert-inner--icon">
            <FiAlertTriangle />
          </span>{' '}
          <span className="alert-inner--text">
            <strong>Uh oh!</strong> You don't have any workspaces yet.
          </span>
        </Alert>
      </div>
    );
  }
  return (
    <Row>
      {workspaces.map((item, index) => (
        <Col
          xs={12}
          sm={12}
          md={6}
          lg={3}
          xl={2}
          className="fadeInUp"
          key={item.workspace.id}
          style={{
            animationDelay: `${(0.05 + Number(index) * 0.025).toString()}s`,
          }}
        >
          <WorkspaceItem key={item.workspace.id} item={item} />
        </Col>
      ))}
    </Row>
  );
}

/**
 * Renders a list of projects with optional loading state and pagination controls.
 *
 * If no projects are provided, an alert is displayed indicating that there are no simulations yet.
 * While data is loading, a skeleton placeholder is shown. Once loaded, each project is rendered
 * using the ProjectItem component within a responsive grid layout. Pagination controls are displayed
 * if multiple pages of projects exist.
 *
 * @param props.projects - Array of user projects to render.
 * @param props.loading - Flag indicating whether project data is currently being fetched.
 * @param props.totalPages - Total number of pagination pages.
 * @param props.params - Pagination parameters including the current page and items per page.
 * @param props.setParams - Function to update the pagination parameters.
 * @param props.handlePageChange - Function to handle changes to the current page.
 *
 * @returns A JSX element representing the project list with loading indicators and pagination if applicable.
 */
function ProjectList(props: {
  projects: UserProject[];
  loading: boolean;
  totalPages: number;
  params: {
    page: number;
    perPage: number;
  };
  setParams: (params: { page: number; perPage: number }) => void;
  handlePageChange: (page: number) => void;
}) {
  const { projects, loading, totalPages, params, setParams, handlePageChange } = props;
  if (projects.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center my-6">
        <Alert color="default">
          <span className="alert-inner--icon">
            <FiAlertTriangle />
          </span>{' '}
          <span className="alert-inner--text">
            <strong>Uh oh!</strong> You don't have any simulations yet.
          </span>
        </Alert>
      </div>
    );
  }
  return (
    <Row className="mt-4 main-container">
      <Col>
        <Row className="m0-2">
          <Col xs={12} sm={12} md={12} lg={12} xl={12}>
            <Row>
              {loading ? (
                loadingSkeleton()
              ) : (
                <>
                  {projects.map((item, index) => (
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      lg={3}
                      xl={2}
                      className="d-flex fadeInUp"
                      key={item.id}
                      style={{
                        animationDelay: `${(0.05 + Number(index) * 0.025).toString()}s`,
                      }}
                    >
                      <ProjectItem key={item.id} project={item} />
                    </Col>
                  ))}
                </>
              )}
            </Row>
          </Col>
          <Col>
            {totalPages > 0 && (
              <FooterPagination
                params={params}
                setParams={setParams}
                handlePageChange={handlePageChange}
                totalPages={totalPages}
              />
            )}
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

/**
 * Renders a card displaying a project's details along with a dropdown menu of actions.
 *
 * The component shows the project's image (linked to its challenge page), workspace name,
 * and project title. It conditionally includes action links for viewing the simulation,
 * viewing a report (if the project is finished), or resuming the simulation (if the project is approved).
 *
 * @param props - An object containing the project to be rendered.
 */
export function ProjectItem(props: { project: UserProject }) {
  const { project: item } = props;
  const currentStageId = item.attributes.currentStage?.id;
  let resumeSimulationUrl = `/workspace/${
    item.attributes.workspace.id
  }/challenge/${item.attributes.project.id.toString()}/`;
  if (currentStageId) {
    resumeSimulationUrl += `${currentStageId}/0/resume`;
  }
  return (
    <Card className="w-100">
      <div className="project__item__imageContainer">
        <Link
          to={`/workspace/${
            item.attributes.workspace.id
          }/challenge/${item.attributes.project.id.toString()}/${slugify(
            item.attributes.project.name
          )}`}
        >
          <Image alt="..." src={item.attributes.project.image} className="project__item__image" />
        </Link>
      </div>
      <CardBody className="pt-2 pb-3 pl-3 pr-2">
        <div className="d-flex">
          <div className="text_overlap">
            {' '}
            <small className="text-muted">{item.attributes.workspace.name}</small>
          </div>
          <div className="ml-auto ">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  className="btn btn-sm"
                  style={{
                    backgroundColor: 'transparent',
                    margin: 0,
                    padding: '0px 8px',
                    boxShadow: 'none',
                    border: 'none',
                  }}
                  aria-label="Customise options"
                >
                  <BsThreeDots size={15} />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className="DropdownMenuContent" sideOffset={5} align="start">
                  <DropdownMenu.Arrow className="DropdownMenuArrow" />
                  <DropdownMenu.Item asChild className="DropdownMenuItem">
                    <Link color="link" to={`/workspace/${item.attributes.workspace.id}`}>
                      <PiArrowSquareInDuotone />
                      <span>{translate('listProjects.viewSimulation')}</span>
                    </Link>
                  </DropdownMenu.Item>
                  {item.attributes.state === 'Finished' ? (
                    <DropdownMenu.Item asChild className="DropdownMenuItem">
                      <Link
                        color="link"
                        to={`/workspace/${
                          item.attributes.workspace.id
                        }/challenge/${item.attributes.project.id.toString()}/report`}
                      >
                        <PiPresentationChartDuotone />
                        <span>{translate('listProjects.viewReport')}</span>
                      </Link>
                    </DropdownMenu.Item>
                  ) : null}
                  {item.attributes.state === 'Approved' ? (
                    <DropdownMenu.Item asChild className="DropdownMenuItem">
                      <Link color="link" to={resumeSimulationUrl}>
                        <PiPlayDuotone />
                        <span>{translate('listProjects.resumeSimulation')}</span>
                      </Link>
                    </DropdownMenu.Item>
                  ) : null}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>
        <h3 className="mb-0 h3 text-wrapping">{i18nCText(item.attributes.project.name)}</h3>
      </CardBody>
    </Card>
  );
}

export default ListWorkspaces;
